import * as THREE from "three";
import { MapControls } from "three/addons/controls/MapControls.js";
import { CSS2DRenderer } from "three/addons/renderers/CSS2DRenderer.js";
import type { AssetBundle } from "@mcp/assets";
import type { DimensionInfo, SourceInit } from "@mcp/world";
import type { MeshBuffers, MeshParams } from "@mcp/mesher";
import { WorkerPool, regionKey } from "./WorkerPool.ts";
import { HeightField } from "./HeightField.ts";
import { SKY, createBlockMaterials, createBlockTexture, createLodTile, createSky, timeOfDay, type LodTile, type SharedUniforms } from "./materials.ts";
import type { MeshResult, RegionMapResult, StructureInfo, StructureMeshResult, StructureVoxels, WorkerBundle } from "./protocol.ts";

export interface EngineSettings {
  renderDistance: number; // chunks
  cutY: number | null;
  /** false = no 3D block meshes, only the region maps (guests without a game jar: "map colours only") */
  detail?: boolean;
}

export interface PickResult {
  x: number;
  y: number; // surface y (top of block)
  z: number;
}

export interface PointerHandler {
  pointerDown?(e: PointerEvent): void;
  hover?(p: PickResult | null, e: PointerEvent): void;
  click?(p: PickResult | null, e: PointerEvent): void;
  dblclick?(p: PickResult | null, e: MouseEvent): void;
  /** return true to take over a drag (controls are disabled until pointerup) */
  dragStart?(p: PickResult | null, e: PointerEvent): boolean;
  drag?(p: PickResult | null, e: PointerEvent): void;
  dragEnd?(p: PickResult | null, e: PointerEvent): void;
}

interface ChunkEntry {
  group: THREE.Group | null;
  gen: number;
}

export interface EngineStats {
  regionsLoaded: number;
  regionsTotal: number;
  chunks: number;
  pending: number;
  triangles: number;
  fps: number;
}

export class Engine {
  readonly renderer: THREE.WebGLRenderer;
  readonly labels: CSS2DRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly controls: MapControls;
  readonly heights = new HeightField();
  readonly planRoot = new THREE.Group();
  readonly overlayRoot = new THREE.Group();
  /** blocks of placed schematics (not pickable — their plan objects are) */
  readonly structRoot = new THREE.Group();
  /** the demo plan drawn on the wizard's backdrop (never pickable) */
  readonly backdropRoot = new THREE.Group();
  onStats: (s: EngineStats) => void = () => {};
  handler: PointerHandler | null = null;

  private container: HTMLElement;
  private pool: WorkerPool | null = null;
  readonly shared: SharedUniforms;
  private materials: ReturnType<typeof createBlockMaterials> | null = null;
  private workerBundle: WorkerBundle | null = null;
  private worldRoot = new THREE.Group();
  private lodRoot = new THREE.Group();
  private lod = new Map<string, LodTile>();
  private presence = new Map<string, Uint8Array>();
  private chunks = new Map<string, ChunkEntry>();
  private inflight = new Set<string>();
  private gen = 0;
  private dim: DimensionInfo | null = null;
  /** the generated demo island (its chunks exist before its map does) */
  private demoWorld = false;
  /** when the current world / backdrop was opened */
  private openedAt = 0;
  private settings: EngineSettings = { renderDistance: 10, cutY: null };
  private raf = 0;
  private lastStream = 0;
  private hoverBox: THREE.LineSegments;
  private disposed = false;
  private mapQueue: { rx: number; rz: number }[] = [];
  private mapsLoaded = 0;
  private frames = 0;
  private fpsT = performance.now();
  private fps = 0;
  private worldSession = 0;
  onMapsChanged: () => void = () => {};

  constructor(container: HTMLElement) {
    this.container = container;
    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance", preserveDrawingBuffer: false });
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace; // shaders output game-space colours directly
    this.renderer.setClearColor(SKY);
    container.appendChild(this.renderer.domElement);
    this.renderer.domElement.style.display = "block";

    this.labels = new CSS2DRenderer();
    this.labels.domElement.className = "label-layer";
    container.appendChild(this.labels.domElement);

    this.camera = new THREE.PerspectiveCamera(50, 1, 0.5, 20000);
    this.camera.position.set(0, 300, 200);
    this.controls = new MapControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.12;
    this.controls.screenSpacePanning = false;
    this.controls.maxPolarAngle = Math.PI * 0.47;
    this.controls.minDistance = 8;
    this.controls.maxDistance = 6000;
    this.controls.zoomToCursor = true;
    this.controls.target.set(0, 64, 0);

    this.shared = {
      uTex: { value: new THREE.DataArrayTexture(new Uint8Array(4), 1, 1, 1) },
      uFogColor: { value: SKY.clone() },
      uFogNear: { value: 400 },
      uFogFar: { value: 2000 },
      uCutY: { value: 1e9 },
      uTime: { value: 0 },
      uLightMix: { value: 0 },
      uDaylight: { value: 1 },
      uSkyLight: { value: new THREE.Color(1, 1, 1) },
      uTorch: { value: new THREE.Color(1.0, 0.82, 0.55) },
      uMinLight: { value: 0.07 },
    };
    this.scene.add(this.sky.mesh);

    this.scene.add(this.lodRoot, this.worldRoot, this.structRoot, this.planRoot, this.overlayRoot, this.backdropRoot);
    this.hoverBox = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(1.02, 1.02, 1.02)),
      new THREE.LineBasicMaterial({ color: 0x111111, transparent: true, opacity: 0.8, depthTest: true }),
    );
    this.hoverBox.visible = false;
    this.overlayRoot.add(this.hoverBox);

    this.bindPointer();
    const ro = new ResizeObserver(() => this.resize());
    ro.observe(container);
    this.resize();
    this.loop();
  }

  // ---------------- assets / world ----------------

  setAssets(bundle: AssetBundle) {
    const tex = createBlockTexture(bundle.textures, this.renderer);
    this.shared.uTex.value.dispose();
    this.shared.uTex.value = tex;
    this.materials ??= createBlockMaterials(this.shared);
    const { pixels: _p, ...texMeta } = bundle.textures;
    this.workerBundle = { ...bundle, textures: texMeta };
    // the backdrop's chunks point at layers of the old texture array: build it again with the new blocks; a bundle
    // without any (map colours only, a guest joining without textures) can't draw it: then it goes (the world comes next)
    if (this.backdrop && this.backdropArgs) {
      if (Object.keys(bundle.blockstates).length) void this.showBackdrop(...this.backdropArgs, true);
      else { this.worldSession++; this.stopBackdrop(); this.clearWorld(); this.dim = null; }
    }
  }

  /**
   * @param prebaked optional region-map loaders ("rx,rz" → fetch) — e.g. maps pre-baked by the host,
   *                 so guests see the whole world without downloading and decoding every region file.
   */
  async openWorld(source: SourceInit, dim: DimensionInfo, focus: [number, number, number], prebaked?: Map<string, () => Promise<RegionMapResult>>) {
    if (!this.workerBundle) throw new Error("Load game assets first");
    const session = ++this.worldSession;
    this.stopBackdrop();
    this.clearWorld();
    this.dim = dim;
    this.demoWorld = source.kind === "demo";
    this.showSea(this.demoWorld);
    this.openedAt = performance.now();
    this.pool ??= new WorkerPool();
    await this.pool.broadcast({ type: "init", bundle: this.workerBundle, source, dim });
    if (session !== this.worldSession) return;
    this.flyTo(focus[0], focus[1], focus[2], 260, false);
    const [fx, fz] = [focus[0] >> 9, focus[2] >> 9];
    const sorted = [...dim.regions].sort((a, b) => Math.hypot(a.rx - fx, a.rz - fz) - Math.hypot(b.rx - fx, b.rz - fz)).map((r) => ({ rx: r.rx, rz: r.rz }));
    this.prebakedQueue = prebaked ? sorted.filter((r) => prebaked.has(`${r.rx},${r.rz}`)).map((r) => prebaked.get(`${r.rx},${r.rz}`)!) : [];
    const rest = prebaked ? sorted.filter((r) => !prebaked.has(`${r.rx},${r.rz}`)) : sorted;
    // "map colours only" (no game jar): regions the host didn't pre-bake can't be coloured — leave them out
    const localMaps = this.settings.detail !== false;
    this.mapQueue = localMaps ? rest : [];
    if (!localMaps) this.mapsLoaded += rest.length;
    this.pumpPrebaked(session);
    // (the demo island's map follows its first chunks: the frame loop pumps it)
    if (!this.demoWorld) this.pumpMaps(session);
    this.lastStream = 0;
  }

  private prebakedQueue: (() => Promise<RegionMapResult>)[] = [];
  private prebakedInflight = 0;
  private pumpPrebaked(session: number) {
    while (session === this.worldSession && this.prebakedInflight < 6 && this.prebakedQueue.length) {
      const load = this.prebakedQueue.shift()!;
      this.prebakedInflight++;
      load()
        .then((res) => { if (session === this.worldSession) this.addRegionMap(res); })
        .catch((e) => console.warn("prebaked map failed", e))
        .finally(() => { this.prebakedInflight--; this.pumpPrebaked(session); });
    }
  }

  /** All region maps of the current dimension are loaded (host can export them). */
  mapsComplete() {
    return !!this.dim && this.mapsLoaded >= this.dim.regions.length;
  }

  /** Region maps currently loaded (colour/height/presence), for sharing. */
  exportRegionMaps(): RegionMapResult[] {
    return [...this.mapData.values()];
  }

  currentDimension() {
    return this.dim?.id ?? null;
  }

  private pumpMaps(session: number) {
    if (!this.pool || session !== this.worldSession) return;
    // the demo island: the blocks around the camera come first (making a region's map keeps a worker busy for a second)
    if (this.demoWorld && this.inflight.size > 0 && performance.now() - this.openedAt < 5000) return;
    for (let w = 0; w < this.pool.size && this.mapQueue.length; w++) {
      if (this.pool.busy(w) >= 1) continue;
      // prefer regions near the current camera target
      const t = this.controls.target;
      const [tx, tz] = [t.x / 512, t.z / 512];
      this.mapQueue.sort((a, b) => Math.hypot(a.rx + 0.5 - tx, a.rz + 0.5 - tz) - Math.hypot(b.rx + 0.5 - tx, b.rz + 0.5 - tz));
      const r = this.mapQueue.shift()!;
      this.pool
        .run<RegionMapResult>(w, { type: "map", rx: r.rx, rz: r.rz })
        .then((res) => {
          if (session !== this.worldSession) return;
          this.addRegionMap(res);
        })
        .catch((e) => console.warn("region map failed", r, e))
        .finally(() => this.pumpMaps(session));
    }
  }

  private mapData = new Map<string, RegionMapResult>();

  private addRegionMap(r: RegionMapResult) {
    const k = `${r.rx},${r.rz}`;
    this.mapData.set(k, r);
    this.presence.set(k, r.presence);
    this.heights.set(r.rx, r.rz, r.height, r.color);
    const tile = createLodTile(r.rx, r.rz, r.color, r.height, this.shared);
    // restore mask for chunks already meshed
    for (const [ck, e] of this.chunks) {
      if (!e.group) continue;
      const [cx, cz] = ck.split(",").map(Number);
      if (cx >> 5 === r.rx && cz >> 5 === r.rz) tile.maskData[(cz & 31) * 32 + (cx & 31)] = 255;
    }
    tile.mask.needsUpdate = true;
    this.lod.set(k, tile);
    this.lodRoot.add(tile.mesh);
    this.mapsLoaded++;
    this.onMapsChanged();
  }

  /** Region map colour data for the minimap. */
  regionMaps() {
    return this.lod;
  }

  clearWorld() {
    for (const e of this.chunks.values()) disposeGroup(e.group);
    this.chunks.clear();
    this.inflight.clear();
    for (const t of this.lod.values()) {
      (t.mesh.material as THREE.Material).dispose();
      t.color.dispose(); t.height.dispose(); t.mask.dispose();
    }
    this.lod.clear();
    this.lodRoot.clear();
    this.worldRoot.clear();
    this.presence.clear();
    this.heights.clear();
    this.mapQueue = [];
    this.prebakedQueue = [];
    this.mapData.clear();
    this.mapsLoaded = 0;
  }

  // ---------------- schematics ----------------

  /** the world worker a schematic's jobs go to (it keeps the parsed file) */
  private structureWorker(hash: string) {
    if (!this.pool) throw new Error("Open a world first.");
    return this.pool.pick(parseInt(hash.slice(0, 8), 16) >>> 0);
  }

  /** Read a schematic file in a worker (size, blocks, material list, preview picture). */
  parseStructure(hash: string, blob: Blob, name: string): Promise<StructureInfo> {
    const w = this.structureWorker(hash);
    return this.pool!.run<StructureInfo>(w, { type: "parseStructure", hash, blob, name });
  }

  /** Mesh a schematic with the world's block models (cutY is structure-local, null = all). */
  meshStructure(hash: string, blob: Blob, name: string, cutY: number | null, lod?: number): Promise<StructureMeshResult> {
    const w = this.structureWorker(hash);
    return this.pool!.run<StructureMeshResult>(w, { type: "meshStructure", hash, blob, name, cutY, ...(lod && lod > 1 ? { lod } : {}) });
  }

  /** Outer blocks of a schematic as cubes (previews without game textures; coarser when there are more than `max`). */
  structureVoxels(hash: string, blob: Blob, name: string, lod: number, max: number): Promise<StructureVoxels> {
    const w = this.structureWorker(hash);
    return this.pool!.run<StructureVoxels>(w, { type: "structureVoxels", hash, blob, name, lod, max });
  }

  // ---------------- lighting / time of day ----------------

  private sky = createSky();
  private lighting = { on: false, ticks: 6000, speed: 0 };
  /** called ~4× a second while the day cycle plays (to move the time slider) */
  onTime: (ticks: number) => void = () => {};
  private lastTimeReport = 0;

  /**
   * @param on     Minecraft light levels + sky (false = the flat, always-bright map look)
   * @param ticks  0 sunrise · 6000 noon · 12000 sunset · 18000 midnight
   * @param speed  ticks per second while playing (0 = paused; 200 ≈ one day in 2 minutes)
   */
  setLighting(l: Partial<{ on: boolean; ticks: number; speed: number }>) {
    Object.assign(this.lighting, l);
    this.applyLighting();
  }

  private applyLighting() {
    const { on, ticks } = this.lighting;
    const t = timeOfDay(ticks);
    const u = this.shared;
    u.uLightMix.value = on ? 1 : 0;
    u.uDaylight.value = t.daylight;
    u.uSkyLight.value.copy(t.skyLight);
    this.sky.mesh.visible = on;
    this.sky.uniforms.uZenith.value.copy(t.zenith);
    this.sky.uniforms.uHorizon.value.copy(t.horizon);
    this.sky.uniforms.uSunDir.value.copy(t.sunDir);
    this.sky.uniforms.uStars.value = t.stars;
    const fog = on ? t.horizon : SKY;
    u.uFogColor.value.copy(fog);
    this.renderer.setClearColor(fog);
  }

  setSettings(s: Partial<EngineSettings>) {
    const cutChanged = s.cutY !== undefined && s.cutY !== this.settings.cutY;
    Object.assign(this.settings, s);
    if (cutChanged) {
      this.gen++;
      this.heights.cutY = this.settings.cutY;
      this.shared.uCutY.value = this.settings.cutY ?? 1e9;
      this.onMapsChanged();
    }
    this.lastStream = 0;
  }

  // ---------------- wizard backdrop & fly-in ----------------

  /** slow orbit around the target (rad/s), while the setup wizard shows the demo island */
  private orbit = 0;
  private backdrop = false;
  private anim: { t0: number; ms: number; from: THREE.Vector3; to: THREE.Vector3 } | null = null;
  private lastBackdropFrame = 0;
  /** frame interval average while the backdrop shows — a slow machine (or software GL) gets a still picture */
  private backdropAvg = 0;
  private backdropFrames = 0;
  private backdropStill = false;

  /**
   * The open sea around the demo island, out to the horizon (just under the island's own water, so it only shows
   * beyond its map): without it the island would end at a hard edge against the sky.
   */
  private sea: THREE.Mesh | null = null;
  private showSea(on: boolean) {
    if (on && !this.sea) {
      const g = new THREE.PlaneGeometry(60000, 60000);
      g.rotateX(-Math.PI / 2);
      const m = new THREE.ShaderMaterial({
        glslVersion: THREE.GLSL3,
        uniforms: { uFogColor: this.shared.uFogColor, uFogNear: this.shared.uFogNear, uFogFar: this.shared.uFogFar, uSea: { value: new THREE.Color(60 / 255, 96 / 255, 166 / 255) } },
        vertexShader: `out float vDist; void main() { vec4 mv = modelViewMatrix * vec4(position, 1.0); vDist = -mv.z; gl_Position = projectionMatrix * mv; }`,
        fragmentShader: `uniform vec3 uFogColor; uniform float uFogNear; uniform float uFogFar; uniform vec3 uSea; in float vDist; out vec4 outColor;
          void main() { outColor = vec4(mix(uSea, uFogColor, smoothstep(uFogNear, uFogFar, vDist)), 1.0); }`,
      });
      this.sea = new THREE.Mesh(g, m);
      this.sea.position.set(0, 62.6, 0);
      this.sea.renderOrder = -2;
      this.sea.frustumCulled = false;
      this.scene.add(this.sea);
    }
    if (this.sea) this.sea.visible = on;
  }

  /** detail radius (chunks) of the backdrop: the island in 3D blocks around the village, the map beyond */
  static readonly BACKDROP_RADIUS = 13;
  private backdropArgs: [DimensionInfo, [number, number, number], number, [number, number, number]] | null = null;

  /**
   * Show the generated demo island (lib/demoWorld.ts) behind the setup wizard: real 3D chunks around the village made
   * by the world workers, its map beyond, under a daylight sky, slowly circling. Needs block assets (setAssets): the
   * built-in blocks or the player's own game textures; when those change, it is built again (`keepCamera`).
   * @param orbit rad/s (0 = still, e.g. for reduced motion)
   * @param sky   colour of the sky at the horizon (the far land fades into it)
   */
  async showBackdrop(dim: DimensionInfo, focus: [number, number, number], orbit: number, sky: [number, number, number], keepCamera = false) {
    if (!this.workerBundle) throw new Error("Load block assets first");
    const session = ++this.worldSession;
    this.clearWorld();
    this.dim = dim;
    this.demoWorld = true;
    this.showSea(true);
    this.openedAt = performance.now();
    this.backdrop = true;
    this.backdropArgs = [dim, focus, orbit, sky];
    // daylight sky; the land fades into its horizon (no visible edge); lower resolution, ~30 fps (it sits behind the wizard)
    const t = timeOfDay(6000);
    const horizon = new THREE.Color(sky[0] / 255, sky[1] / 255, sky[2] / 255);
    this.sky.uniforms.uZenith.value.copy(t.zenith);
    this.sky.uniforms.uHorizon.value.copy(horizon);
    this.sky.uniforms.uSunDir.value.copy(t.sunDir);
    this.sky.uniforms.uStars.value = 0;
    this.sky.mesh.visible = true;
    this.shared.uFogColor.value.copy(horizon);
    this.shared.uLightMix.value = 0;
    this.renderer.setClearColor(horizon);
    this.renderer.setPixelRatio(Math.min(1, window.devicePixelRatio));
    this.resize();
    if (!keepCamera) {
      this.backdropAvg = 0;
      this.backdropFrames = 0;
      this.backdropStill = false;
      this.controls.target.set(focus[0], focus[1], focus[2]);
      this.camera.position.set(focus[0] + 118, focus[1] + 84, focus[2] + 150);
      this.orbit = orbit;
      this.anim = null;
    }
    this.pool ??= new WorkerPool();
    await this.pool.broadcast({ type: "init", bundle: this.workerBundle, source: { kind: "demo" }, dim });
    if (session !== this.worldSession) return;
    const [fx, fz] = [focus[0] >> 9, focus[2] >> 9];
    this.mapQueue = [...dim.regions].sort((a, b) => Math.hypot(a.rx - fx, a.rz - fz) - Math.hypot(b.rx - fx, b.rz - fz)).map((r) => ({ rx: r.rx, rz: r.rz }));
    // the 3D blocks around the village first, the map of the rest when they're there (see pumpMaps)
    this.lastStream = 0;
  }

  stopBackdrop() {
    if (!this.backdrop) return;
    this.backdrop = false;
    this.backdropArgs = null;
    // what's drawn stays until the next world replaces it, but no new chunks or maps of the island are asked for
    this.mapQueue = [];
    this.dim = null;
    this.orbit = 0;
    this.backdropRoot.clear();
    this.applyLighting(); // fog + clear colour + sky back to the planner's own
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    this.resize();
  }

  get showingBackdrop() {
    return this.backdrop;
  }

  /** the machine draws the circling backdrop smoothly (the wizard then affords its frosted-glass card) */
  get backdropFast() {
    return this.backdrop && !this.backdropStill && this.backdropFrames >= 15 && this.backdropAvg < 45;
  }

  /** "orbit" normally, "still" on a machine too slow for it (or with reduced motion) */
  get backdropMode() {
    return this.backdropStill || !this.orbit ? "still" : "orbit";
  }

  /**
   * Camera flight into the current view: starts high above the map and ends where the camera is now
   * (after openWorld's flyTo). Any mouse, wheel or key input stops it.
   */
  flyIn(ms = 2500) {
    const t = this.controls.target;
    const to = this.camera.position.clone().sub(t);
    const from = new THREE.Vector3(to.x * 0.15, Math.max(1400, to.length() * 5), to.z * 0.15 + 40);
    this.camera.position.copy(t).add(from);
    this.anim = { t0: performance.now(), ms, from, to };
  }

  get flying() {
    return !!this.anim;
  }

  private stepAnim(now: number) {
    const a = this.anim;
    if (!a) return;
    const k = Math.min(1, (now - a.t0) / a.ms);
    const e = k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2; // ease in-out
    const dir = a.from.clone().normalize().lerp(a.to.clone().normalize(), e).normalize();
    const len = Math.exp(Math.log(a.from.length()) + (Math.log(a.to.length()) - Math.log(a.from.length())) * e);
    this.camera.position.copy(this.controls.target).addScaledVector(dir, len);
    this.lastStream = Math.min(this.lastStream, now - 60);
    if (k >= 1) this.anim = null;
  }

  /** Screen position (px, relative to the map) of a world point, or null when it's behind the camera. */
  project(x: number, y: number, z: number): { x: number; y: number } | null {
    const v = new THREE.Vector3(x, y, z).project(this.camera);
    if (v.z > 1) return null;
    const w = this.container.clientWidth, h = this.container.clientHeight;
    return { x: (v.x + 1) / 2 * w, y: (1 - v.y) / 2 * h };
  }

  /** where the camera looks at and how far away it is (for the tour: "did they move / zoom?") */
  view() {
    const t = this.controls.target;
    return { x: t.x, z: t.z, dist: this.camera.position.distanceTo(t) };
  }

  flyTo(x: number, y: number, z: number, dist = 200, keepAngle = true) {
    const off = keepAngle ? this.camera.position.clone().sub(this.controls.target).setLength(dist) : new THREE.Vector3(0, dist * 0.8, dist * 0.6);
    this.controls.target.set(x, y, z);
    this.camera.position.copy(this.controls.target).add(off);
    this.lastStream = 0;
  }

  setTopDown(on: boolean) {
    const t = this.controls.target;
    const d = this.camera.position.distanceTo(t);
    if (on) {
      this.camera.position.set(t.x, t.y + d, t.z + 0.01);
    } else {
      this.camera.position.set(t.x, t.y + d * 0.75, t.z + d * 0.66);
    }
  }

  // ---------------- map buttons: zoom, compass ----------------

  private zoomAnim: { t0: number; from: number; to: number } | null = null;
  private turnAnim: { t0: number; from: number; delta: number } | null = null;

  /** The + / − buttons on the map: f < 1 comes closer, f > 1 goes further (eased, ~220 ms). */
  zoomBy(f: number) {
    const d = this.camera.position.distanceTo(this.controls.target);
    const base = this.zoomAnim?.to ?? d;
    const to = Math.min(this.controls.maxDistance, Math.max(this.controls.minDistance, base * f));
    this.anim = null;
    this.zoomAnim = { t0: performance.now(), from: d, to };
  }

  /** Which way the view faces, in degrees clockwise from north (−Z): 0 = north, 90 = east. For the compass. */
  heading(): number {
    const t = this.controls.target, cam = this.camera.position;
    let fx = t.x - cam.x, fz = t.z - cam.z;
    if (Math.hypot(fx, fz) < 1e-4) {
      const up = new THREE.Vector3(0, 1, 0).applyQuaternion(this.camera.quaternion);
      fx = up.x; fz = up.z;
    }
    return (Math.atan2(fx, -fz) * 180) / Math.PI;
  }

  /** The compass: turn the view back to face north, keeping height and distance (eased, ~420 ms). */
  faceNorth() {
    const t = this.controls.target, cam = this.camera.position;
    const a = Math.atan2(cam.x - t.x, cam.z - t.z); // 0 = camera south of the target = facing north
    if (Math.abs(a) < 1e-3) return;
    this.anim = null;
    this.turnAnim = { t0: performance.now(), from: a, delta: -a };
  }

  private stepView(now: number) {
    const t = this.controls.target, cam = this.camera.position;
    const z = this.zoomAnim;
    if (z) {
      const k = Math.min(1, (now - z.t0) / 220), e = 1 - Math.pow(1 - k, 3);
      const d = Math.exp(Math.log(z.from) + (Math.log(z.to) - Math.log(z.from)) * e);
      cam.sub(t).setLength(d).add(t);
      if (k >= 1) this.zoomAnim = null;
      this.lastStream = Math.min(this.lastStream, now - 60);
    }
    const r = this.turnAnim;
    if (r) {
      const k = Math.min(1, (now - r.t0) / 420), e = k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;
      const a = r.from + r.delta * e, h = Math.hypot(cam.x - t.x, cam.z - t.z);
      cam.x = t.x + h * Math.sin(a);
      cam.z = t.z + h * Math.cos(a);
      if (k >= 1) this.turnAnim = null;
      this.lastStream = Math.min(this.lastStream, now - 60);
    }
  }

  // ---------------- chunk streaming ----------------

  private streamChunks() {
    if (!this.pool || !this.dim || !this.materials) return;
    const t = this.controls.target;
    const camDist = this.camera.position.distanceTo(t);
    // detail off: nothing is wanted and every loaded chunk is dropped below
    const R = this.backdrop ? (this.backdropStill ? 6 : Engine.BACKDROP_RADIUS) : this.settings.detail === false ? -Infinity : camDist > 1400 ? 0 : Math.max(0, Math.round(this.settings.renderDistance * (camDist > 700 ? 0.5 : 1)));
    const tcx = Math.floor(t.x / 16), tcz = Math.floor(t.z / 16);
    const wanted: [number, number, number][] = [];
    for (let dz = -R; dz <= R; dz++)
      for (let dx = -R; dx <= R; dx++) {
        const d = Math.hypot(dx, dz);
        if (d > R + 0.5) continue;
        const cx = tcx + dx, cz = tcz + dz;
        const p = this.presence.get(`${cx >> 5},${cz >> 5}`);
        const known = p ? !!p[(cz & 31) * 32 + (cx & 31)] : this.demoWorld && this.dim.regions.some((r) => r.rx === cx >> 5 && r.rz === cz >> 5);
        if (!known) continue;
        wanted.push([cx, cz, d]);
      }
    wanted.sort((a, b) => a[2] - b[2]);
    const params: MeshParams = { margin: 3, cutY: this.settings.cutY, sliceDepth: 48 };
    for (const [cx, cz] of wanted) {
      const k = `${cx},${cz}`;
      const e = this.chunks.get(k);
      if ((e && e.gen === this.gen) || this.inflight.has(k)) continue;
      const w = this.pool.pick(regionKey(cx >> 5, cz >> 5));
      if (this.pool.busy(w) >= 3) continue;
      this.inflight.add(k);
      const gen = this.gen;
      const session = this.worldSession;
      this.pool
        .run<MeshResult | null>(w, { type: "mesh", cx, cz, params })
        .then((m) => { if (session === this.worldSession) this.addChunk(cx, cz, m, gen); })
        .catch((err) => console.warn("mesh failed", cx, cz, err))
        .finally(() => this.inflight.delete(k));
    }
    // unload far chunks (hysteresis)
    for (const [k, e] of this.chunks) {
      const [cx, cz] = k.split(",").map(Number);
      if (Math.hypot(cx - tcx, cz - tcz) > R + 2.5) {
        disposeGroup(e.group);
        this.chunks.delete(k);
        this.setMask(cx, cz, false);
      }
    }
  }

  private addChunk(cx: number, cz: number, m: MeshResult | null, gen: number) {
    const k = `${cx},${cz}`;
    const old = this.chunks.get(k);
    if (old && old.gen === this.gen && gen !== this.gen) return; // stale
    if (old) disposeGroup(old.group);
    let group: THREE.Group | null = null;
    if (m && (m.solid.count || m.translucent.count)) {
      group = new THREE.Group();
      group.position.set(cx * 16, 0, cz * 16);
      const add = (b: MeshBuffers, mat: THREE.Material, order: number) => {
        if (!b.count) return;
        const g = new THREE.BufferGeometry();
        g.setAttribute("position", new THREE.BufferAttribute(b.pos, 3));
        g.setAttribute("uvl", new THREE.BufferAttribute(b.uvl, 4));
        g.setAttribute("col", new THREE.BufferAttribute(b.col, 4, true));
        g.setIndex(new THREE.BufferAttribute(b.indices, 1));
        const cy = (m.yMin + m.yMax) / 2;
        g.boundingSphere = new THREE.Sphere(new THREE.Vector3(8, cy, 8), Math.hypot(12, (m.yMax - m.yMin) / 2 + 2));
        const mesh = new THREE.Mesh(g, mat);
        mesh.renderOrder = order;
        mesh.matrixAutoUpdate = false;
        group!.add(mesh);
      };
      add(m.solid, this.materials!.solid, 0);
      add(m.translucent, this.materials!.translucent, 1);
      group.updateMatrixWorld(true);
      this.worldRoot.add(group);
    }
    this.chunks.set(k, { group, gen });
    this.setMask(cx, cz, !!group);
  }

  private setMask(cx: number, cz: number, on: boolean) {
    const t = this.lod.get(`${cx >> 5},${cz >> 5}`);
    if (!t) return;
    t.maskData[(cz & 31) * 32 + (cx & 31)] = on ? 255 : 0;
    t.mask.needsUpdate = true;
  }

  // ---------------- input ----------------

  private pick(e: { clientX: number; clientY: number }): PickResult | null {
    const r = this.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    const rc = new THREE.Raycaster();
    rc.setFromCamera(ndc, this.camera);
    return this.heights.pick(rc.ray);
  }

  /** Raycast plan objects (for selection). */
  raycastPlans(e: { clientX: number; clientY: number }): THREE.Intersection[] {
    const r = this.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    const rc = new THREE.Raycaster();
    rc.params.Line = { threshold: 1.5 };
    rc.setFromCamera(ndc, this.camera);
    return rc.intersectObjects(this.planRoot.children, true);
  }

  /** Re-dispatch a pointer/wheel event that happened on an HTML overlay (e.g. a name tag) to the 3D canvas. */
  forwardToCanvas(ev: PointerEvent | WheelEvent) {
    const el = this.renderer.domElement;
    const copy = ev instanceof WheelEvent ? new WheelEvent(ev.type, ev) : new PointerEvent(ev.type, ev);
    el.dispatchEvent(copy);
  }

  private bindPointer() {
    const el = this.renderer.domElement;
    let down: { x: number; y: number; button: number } | null = null;
    let dragging = false;
    let hoverQueued: PointerEvent | null = null;
    // the user takes over: stop a fly-in
    el.addEventListener("wheel", () => { this.anim = null; this.zoomAnim = null; this.turnAnim = null; }, { passive: true, capture: true });
    el.addEventListener("pointerdown", (e) => {
      this.anim = null;
      this.zoomAnim = null;
      this.turnAnim = null;
      down = { x: e.clientX, y: e.clientY, button: e.button };
      this.handler?.pointerDown?.(e);
      if (e.button === 0 && this.handler?.dragStart?.(this.pick(e), e)) {
        dragging = true;
        this.controls.enabled = false;
        el.setPointerCapture(e.pointerId);
      }
    });
    el.addEventListener("pointermove", (e) => {
      if (dragging) { this.handler?.drag?.(this.pick(e), e); return; }
      if (!hoverQueued) requestAnimationFrame(() => {
        const ev = hoverQueued!;
        hoverQueued = null;
        const p = this.pick(ev);
        this.showHover(p);
        this.handler?.hover?.(p, ev);
      });
      hoverQueued = e;
    });
    el.addEventListener("pointerup", (e) => {
      const p = this.pick(e);
      if (dragging) {
        dragging = false;
        this.controls.enabled = true;
        this.handler?.dragEnd?.(p, e);
        down = null;
        return;
      }
      if (down && down.button === 0 && Math.hypot(e.clientX - down.x, e.clientY - down.y) < 5) this.handler?.click?.(p, e);
      down = null;
    });
    el.addEventListener("dblclick", (e) => this.handler?.dblclick?.(this.pick(e), e));
    el.addEventListener("pointerleave", () => this.showHover(null));
    el.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  private showHover(p: PickResult | null) {
    this.hoverBox.visible = !!p;
    if (p) this.hoverBox.position.set(p.x + 0.5, p.y - 0.5, p.z + 0.5);
  }

  // ---------------- loop ----------------

  private resize() {
    const w = this.container.clientWidth || 1, h = this.container.clientHeight || 1;
    this.renderer.setSize(w, h);
    this.labels.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  // ---------------- keyboard navigation ----------------

  private keys = new Set<string>();
  private lastFrame = performance.now();

  /** Keys that move the camera (physical positions, so QWERTZ/AZERTY work the same). */
  static readonly NAV_KEYS = new Set([
    "KeyW", "KeyA", "KeyS", "KeyD", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight",
    "KeyQ", "KeyE", "Equal", "Minus", "NumpadAdd", "NumpadSubtract", "PageUp", "PageDown", "ShiftLeft", "ShiftRight",
  ]);

  /** @returns true if the key is a navigation key (caller should preventDefault) */
  setKey(code: string, down: boolean) {
    if (!Engine.NAV_KEYS.has(code)) return false;
    if (down) this.keys.add(code); else this.keys.delete(code);
    return !code.startsWith("Shift");
  }

  clearKeys() {
    this.keys.clear();
  }

  /** WASD / arrows pan along the ground relative to the view, Q/E rotate, +/- PageUp/PageDown zoom. */
  private applyKeys(dt: number) {
    if (!this.keys.size) return;
    this.anim = null;
    this.zoomAnim = null;
    this.turnAnim = null;
    const k = (c: string) => this.keys.has(c);
    const t = this.controls.target, cam = this.camera.position;
    const dist = cam.distanceTo(t);
    const fast = k("ShiftLeft") || k("ShiftRight") ? 3 : 1;
    // pan speed scales with zoom so it feels the same close up and far away
    const speed = Math.max(12, dist * 0.9) * fast * dt;
    let fx = t.x - cam.x, fz = t.z - cam.z;
    const fl = Math.hypot(fx, fz);
    if (fl < 1e-3) { // straight top-down: "forward" = north on screen (camera up vector)
      const up = new THREE.Vector3(0, 1, 0).applyQuaternion(this.camera.quaternion);
      fx = up.x; fz = up.z;
    }
    const n = Math.hypot(fx, fz) || 1;
    fx /= n; fz /= n;
    const rx = -fz, rz = fx; // right-hand vector on the ground
    let mx = 0, mz = 0;
    if (k("KeyW") || k("ArrowUp")) { mx += fx; mz += fz; }
    if (k("KeyS") || k("ArrowDown")) { mx -= fx; mz -= fz; }
    if (k("KeyD") || k("ArrowRight")) { mx += rx; mz += rz; }
    if (k("KeyA") || k("ArrowLeft")) { mx -= rx; mz -= rz; }
    if (mx || mz) {
      const l = Math.hypot(mx, mz);
      const dx = (mx / l) * speed, dz = (mz / l) * speed;
      t.x += dx; t.z += dz; cam.x += dx; cam.z += dz;
      // keep the target near the ground so orbiting still feels right
      const gy = this.heights.at(t.x, t.z);
      if (gy !== null) { const dy = (gy - t.y) * Math.min(1, dt * 4); t.y += dy; cam.y += dy; }
    }
    const rot = (k("KeyE") ? 1 : 0) - (k("KeyQ") ? 1 : 0);
    if (rot) {
      const a = rot * dt * 1.6 * fast;
      const ox = cam.x - t.x, oz = cam.z - t.z;
      cam.x = t.x + ox * Math.cos(a) - oz * Math.sin(a);
      cam.z = t.z + ox * Math.sin(a) + oz * Math.cos(a);
    }
    const zoom = (k("Minus") || k("NumpadSubtract") || k("PageDown") ? 1 : 0) - (k("Equal") || k("NumpadAdd") || k("PageUp") ? 1 : 0);
    if (zoom) {
      const f = Math.exp(zoom * dt * 1.5 * fast);
      const nd = Math.min(this.controls.maxDistance, Math.max(this.controls.minDistance, dist * f));
      cam.sub(t).setLength(nd).add(t);
    }
    this.lastStream = Math.min(this.lastStream, performance.now() - 60);
  }

  private loop = () => {
    if (this.disposed || this.paused) return;
    this.raf = requestAnimationFrame(this.loop);
    const frameNow = performance.now();
    const dt = Math.min(0.1, (frameNow - this.lastFrame) / 1000);
    this.applyKeys(dt);
    if (this.anim) this.stepAnim(frameNow);
    if (this.zoomAnim || this.turnAnim) this.stepView(frameNow);
    if (this.orbit && !this.anim) {
      const t = this.controls.target, cam = this.camera.position;
      const a = this.orbit * dt, ox = cam.x - t.x, oz = cam.z - t.z;
      cam.x = t.x + ox * Math.cos(a) - oz * Math.sin(a);
      cam.z = t.z + ox * Math.sin(a) + oz * Math.cos(a);
    }
    if (this.lighting.on && this.lighting.speed) {
      this.lighting.ticks = (this.lighting.ticks + dt * this.lighting.speed) % 24000;
      this.applyLighting();
      if (frameNow - this.lastTimeReport > 250) { this.lastTimeReport = frameNow; this.onTime(this.lighting.ticks); }
    }
    this.lastFrame = frameNow;
    this.controls.update();
    const now = performance.now();
    if (now - this.lastStream > 120) {
      this.lastStream = now;
      this.streamChunks();
      if (this.pool && this.mapQueue.length) this.pumpMaps(this.worldSession);
    }
    const dist = this.camera.position.distanceTo(this.controls.target);
    const detail = this.settings.renderDistance * 16;
    this.shared.uFogNear.value = Math.max(detail * 1.5, dist * 1.6);
    this.shared.uFogFar.value = Math.max(detail * 5, dist * 6);
    if (this.backdrop) {
      // the far land (the map beyond the 3D blocks) fades into the sky
      this.shared.uFogNear.value = dist * 1.05;
      this.shared.uFogFar.value = dist * 2.3;
      const gap = now - this.lastBackdropFrame;
      // circling: ~30 fps is plenty; standing still (reduced motion, slow machine): refresh now and then
      if (gap < (this.orbit ? 33 : 1500)) return;
      if (this.lastBackdropFrame && this.orbit) {
        this.backdropAvg = this.backdropFrames ? this.backdropAvg * 0.7 + gap * 0.3 : gap;
        // can't keep ~12 fps: stop circling so the page stays responsive
        if (++this.backdropFrames > 6 && this.backdropAvg > 85) { this.backdropStill = true; this.orbit = 0; }
      }
      this.lastBackdropFrame = now;
    }
    this.labels.domElement.classList.toggle("far", dist > 160);
    this.camera.far = Math.max(3000, dist * 8);
    this.camera.near = Math.max(0.3, dist / 400);
    this.camera.updateProjectionMatrix();
    this.renderer.render(this.scene, this.camera);
    this.labels.render(this.scene, this.camera);
    this.frames++;
    if (now - this.fpsT > 1000) {
      this.fps = (this.frames * 1000) / (now - this.fpsT);
      this.frames = 0;
      this.fpsT = now;
      let loaded = 0;
      for (const e of this.chunks.values()) if (e.group) loaded++;
      this.onStats({
        regionsLoaded: this.mapsLoaded,
        regionsTotal: this.dim?.regions.length ?? 0,
        chunks: loaded,
        pending: this.inflight.size,
        triangles: this.renderer.info.render.triangles,
        fps: Math.round(this.fps),
      });
    }
  };

  private paused = false;

  /** Stop / restart drawing (the marketing site's live demo pauses while it's scrolled off screen). */
  setPaused(paused: boolean) {
    if (paused === this.paused || this.disposed) return;
    this.paused = paused;
    cancelAnimationFrame(this.raf);
    if (!paused) {
      this.lastFrame = performance.now();
      this.loop();
    }
  }

  dispose() {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.clearWorld();
    this.pool?.dispose();
    this.controls.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
    this.labels.domElement.remove();
  }
}

function disposeGroup(g: THREE.Group | null) {
  if (!g) return;
  g.removeFromParent();
  g.traverse((o) => {
    if ((o as THREE.Mesh).geometry) (o as THREE.Mesh).geometry.dispose();
  });
}
